import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {
  DateRangeParamFormat,
  formatDateRangeParams,
  matchDayShortcut,
  normalizeSelectedRange,
  parseDateRangeParam,
} from "@/components/datePicker/DatePickerURLRangeParams.utils.ts";
import {
  DateRangeOption,
  getDatepickerStandardRangeOption,
  getReportsStandardRangeOption,
} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {RequiredDateRange} from "@/components/datetime/utils.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {getCurrentTimezone} from "@/lib/types/datetime.ts";
import {isBefore} from "date-fns";
import {useEffect, useState} from "react";
import {DateRange, TZDate} from "react-day-picker";
import {NavigateOptions, useSearchParams} from "react-router";

//if either from or to is missing we return null
const rangeFromUrl = (
  fromKey: string,
  toKey: string,
  fixedRangeKey: string,
  query: URLSearchParams,
  withReportRanges: boolean = false,
  timezone: string,
  paramFormat: DateRangeParamFormat = "iso"
): RequiredDateRange | null => {
  const urlFrom = query.get(fromKey);
  const urlTo = query.get(toKey);

  const fixedRangeOption = withReportRanges
    ? getDatepickerStandardRangeOption(query.get(fixedRangeKey)) ||
      getReportsStandardRangeOption(query.get(fixedRangeKey))
    : getDatepickerStandardRangeOption(query.get(fixedRangeKey));
  if (fixedRangeOption) {
    const now = timezone ? TZDate.tz(timezone) : new Date();
    return {from: fixedRangeOption.getStartDate(now), to: fixedRangeOption.getEndDate(now)};
  }

  const from = parseDateRangeParam(urlFrom, "start", timezone, paramFormat);
  const to = parseDateRangeParam(urlTo, "end", timezone, paramFormat);
  return from && to ? ({from, to} as RequiredDateRange) : null;
};

/** A static shortcut, or a range the caller computes — status pages derive theirs from page settings. */
export type DateRangeDefault = DateRangeOption | (() => RequiredDateRange);

const resolveDefault = (timezone: string, withDefault: DateRangeDefault): RequiredDateRange => {
  if (typeof withDefault === "function") {
    return withDefault();
  }
  const now = timezone ? TZDate.tz(timezone) : new Date();
  return {
    from: withDefault.getStartDate(now),
    to: withDefault.getEndDate(now),
  };
};

function getInitialDateRange(
  fromKey: string,
  toKey: string,
  fixedRangeKey: string,
  query: URLSearchParams,
  withReportRanges: boolean = false,
  timezone: string,
  defaultRange: DateRangeDefault,
  paramFormat: DateRangeParamFormat = "iso"
): RequiredDateRange {
  const range = rangeFromUrl(fromKey, toKey, fixedRangeKey, query, withReportRanges, timezone, paramFormat);
  if (range && isBefore(range.from, range.to)) {
    return range;
  }
  return resolveDefault(timezone, defaultRange);
}

interface UseDateRangeParams {
  fromKey: string;
  toKey: string;
  fixedRangeKey: string;
  withReportRangeOptions?: boolean;
  withDefault: DateRangeDefault;
  timezone?: string;
  /**
   * How from/to are written. Defaults to full ISO instants; "legacyDays" writes the compact
   * yyyyMMdd form for whole-day ranges, which is what the legacy status page reads.
   */
  paramFormat?: DateRangeParamFormat;
  /** Passed straight to setSearchParams — status pages replace rather than stack history entries. */
  navigateOptions?: NavigateOptions;
  /**
   * Widen day-granular selections to whole days before writing them, so the calendar and the
   * "Last N days" shortcuts produce the same window. See {@link normalizeSelectedRange}.
   */
  normalizeSelection?: boolean;
  /**
   * Derive `fixedRangeOption` from the resolved dates instead of reading it from the URL. For
   * callers that store concrete dates and still want the picker to show "Last 7 Days".
   */
  deriveFixedRange?: boolean;
}

const isSameRange = (a: RequiredDateRange, b: RequiredDateRange): boolean =>
  a.from.getTime() === b.from.getTime() && a.to.getTime() === b.to.getTime();

export const useDateRange = ({
  fromKey,
  toKey,
  fixedRangeKey,
  withReportRangeOptions = false,
  withDefault,
  timezone,
  paramFormat = "iso",
  navigateOptions,
  normalizeSelection = false,
  deriveFixedRange = false,
}: UseDateRangeParams): {
  dateRange: typeof withDefault extends undefined ? RequiredDateRange | null : RequiredDateRange;
  currentParamDateRange: typeof withDefault extends undefined ? RequiredDateRange | null : RequiredDateRange;
  fixedRangeOption: DateRangeOption | undefined;
  handleRangeChange: (newRange: DateRange | null, fixedRange?: StandardDateRange | null) => void;
} => {
  const {user} = useCurrentUserContext();
  const [queryParams, setQueryParams] = useSearchParams();

  // Optional chaining, not assertions: public status pages mount without a UserContextProvider, so
  // there is no user to read a timezone off. They pass one in instead.
  if (!timezone) {
    timezone = user?.account?.timezone ?? getCurrentTimezone();
  }

  const [dateRange, setDateRange] = useState<RequiredDateRange>(
    getInitialDateRange(
      fromKey,
      toKey,
      fixedRangeKey,
      queryParams,
      withReportRangeOptions,
      timezone,
      withDefault,
      paramFormat
    )
  );
  const [currentParamDateRange, setCurrentParamDateRange] = useState<RequiredDateRange>(dateRange);

  useEffect(() => {
    const newDateRange = getInitialDateRange(
      fromKey,
      toKey,
      fixedRangeKey,
      queryParams,
      withReportRangeOptions,
      timezone,
      withDefault,
      paramFormat
    );
    // Keeping the previous object when the window is unchanged lets React bail out: without it, an
    // un-memoized `withDefault` re-fires this effect every render and the fresh object loops.
    setCurrentParamDateRange((prev) => (isSameRange(prev, newDateRange) ? prev : newDateRange));
    setDateRange((prev) => (isSameRange(prev, newDateRange) ? prev : newDateRange));
  }, [fixedRangeKey, fromKey, paramFormat, queryParams, timezone, toKey, withDefault, withReportRangeOptions]);

  const fixedRangeOption = deriveFixedRange
    ? getDatepickerStandardRangeOption(matchDayShortcut(dateRange.from, dateRange.to, timezone))
    : getDatepickerStandardRangeOption(queryParams.get(fixedRangeKey)) ||
      getReportsStandardRangeOption(queryParams.get(fixedRangeKey));

  const handleRangeChange = (newRange: DateRange | null, fixedRange?: StandardDateRange | null) => {
    const params = new URLSearchParams(queryParams);
    const newFixedRange = getDatepickerStandardRangeOption(fixedRange);

    if (newFixedRange) {
      const now = timezone ? TZDate.tz(timezone) : new Date();
      params.delete(fromKey);
      params.delete(toKey);
      params.set(fixedRangeKey, newFixedRange.value);
      setDateRange({
        from: newFixedRange.getStartDate(now),
        to: newFixedRange.getEndDate(now),
      });
    } else {
      if (!newRange || !newRange.from || !newRange.to) {
        params.delete(fixedRangeKey);
        params.delete(fromKey);
        params.delete(toKey);
        setDateRange(resolveDefault(timezone, withDefault));
      } else {
        const selected = normalizeSelection
          ? normalizeSelectedRange(newRange.from, newRange.to)
          : {from: newRange.from, to: newRange.to};
        const written = formatDateRangeParams(selected.from, selected.to, paramFormat, timezone);
        params.delete(fixedRangeKey);
        params.set(fromKey, written.from);
        params.set(toKey, written.to);
        setDateRange(selected);
      }
    }
    setQueryParams(params, navigateOptions);
  };

  return {
    dateRange: dateRange,
    currentParamDateRange,
    fixedRangeOption,
    handleRangeChange,
  };
};
